import React from "react";
import { useReveal } from "@/hooks/useReveal";
import { ArrowRight } from "lucide-react";

const WHATSAPP =
  "https://wa.me/351912952121?text=Ol%C3%A1%20Lu.ft%2C%20gostaria%20de%20saber%20mais%20sobre%20o%20Lu.ft%20Strength.";

export default function Strength() {
  const ref = useReveal();

  return (
    <section
      id="strength"
      ref={ref}
      className="relative overflow-hidden bg-[var(--luft-bg)] pt-32 sm:pt-44 lg:pt-52 pb-32 sm:pb-44 lg:pb-52"
      data-testid="strength-section"
    >
      {/* Ambient sky/blue light — coastal atmosphere */}
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[70vw] h-[70vw] rounded-full opacity-[0.32]"
        style={{
          background:
            "radial-gradient(circle, rgba(181,199,211,0.75) 0%, transparent 65%)",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-40 -left-32 w-[55vw] h-[55vw] rounded-full opacity-[0.22]"
        style={{
          background:
            "radial-gradient(circle, rgba(82,96,254,0.28) 0%, transparent 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* Header row */}
        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-16 items-end mb-20 reveal">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-4 mb-8">
              <p className="eyebrow">03 — Lu.ft Strength</p>
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--luft-ink)]/25 text-[var(--luft-ink-soft)]"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "0.58rem",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                }}
                data-testid="strength-soon-badge"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: "var(--luft-yellow)" }}
                  aria-hidden="true"
                />
                em breve
              </span>
            </div>
            <h2
              className="editorial-heading text-[var(--luft-ink)] text-[clamp(2.4rem,6vw,5rem)] leading-[0.98]"
              data-testid="strength-title"
            >
              Força com <em className="editorial-italic">intenção</em>.
            </h2>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <p className="font-body font-light text-[var(--luft-ink-soft)] text-[1.02rem] leading-relaxed max-w-md">
              Uma prática orientada para desenvolver força, estabilidade e
              capacidade física — a complementar o Pilates e a preparar o corpo
              para se mover melhor, dentro e fora do estúdio.
            </p>
          </div>
        </div>

        {/* Overlapping composition: two images with intentional asymmetry */}
        <div className="relative grid grid-cols-12 gap-y-10 lg:gap-x-8 items-start">
          <div className="col-span-12 md:col-span-7 lg:col-span-6 reveal">
            <div
              className="relative overflow-hidden shadow-[0_18px_60px_-30px_rgba(31,28,21,0.35)]"
              style={{ borderRadius: "var(--radius-organic)" }}
            >
              <img
                src="/media/luft-detail-shoulder.jpg"
                alt="Trabalho de força consciente"
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>

          <div className="col-span-12 md:col-span-5 lg:col-span-5 lg:col-start-8 md:mt-16 lg:mt-32 reveal">
            <div
              className="relative overflow-hidden shadow-[0_18px_60px_-30px_rgba(31,28,21,0.3)]"
              style={{ borderRadius: "var(--radius-organic)" }}
            >
              <img
                src="/media/luft-reformer-arch.jpg"
                alt="Movimento com resistência"
                loading="lazy"
                className="w-full aspect-[3/4] object-cover"
              />
            </div>

            <div className="mt-10 space-y-6 max-w-md">
              <p className="font-display italic text-2xl sm:text-3xl text-[var(--luft-ink)] leading-snug">
                "Não é a força que se demonstra.<br />É a força que se habita."
              </p>

              <ul className="pt-6 border-t border-[var(--luft-line)] space-y-3.5 text-[var(--luft-ink-soft)] font-body font-light text-[0.95rem]">
                <li className="flex items-baseline gap-3">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 bg-[var(--luft-ink)]/40"
                    aria-hidden="true"
                  />
                  Estabilidade e controlo articular
                </li>
                <li className="flex items-baseline gap-3">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 bg-[var(--luft-ink)]/40"
                    aria-hidden="true"
                  />
                  Progressão pensada, sem impacto excessivo
                </li>
                <li className="flex items-baseline gap-3">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 bg-[var(--luft-ink)]/40"
                    aria-hidden="true"
                  />
                  Complementar ao Pilates e à vida ativa
                </li>
              </ul>

              <a
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 mt-4 text-[var(--luft-ink)] text-[0.72rem] uppercase tracking-[0.28em] border-b border-[var(--luft-ink)]/40 pb-2 hover:text-[var(--luft-blue)] hover:border-[var(--luft-blue)] transition-colors"
                data-testid="strength-notify-cta"
              >
                Quero ser avisada(o)
                <ArrowRight size={14} strokeWidth={1.4} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
